import axios from "axios"
import config from "../config.js"

const parseAddress = (addressString, workOrderFields) => {
	// Handle empty or invalid input
	if (!addressString || typeof addressString !== "string") {
		return {
			address: "",
			city: "",
			state: "",
			zip: "",
		}
	}

	// Split the address by commas
	const parts = addressString.split(",").map((part) => part.trim())

	// Extract address and city
	const address = parts[0] || ""
	const city = parts[1] || ""

	// Handle state and zip
	let state = ""
	let zip = ""

	// Check for state and zip in parts[2]
	if (parts[2]) {
		// Check if the 3rd part contains state and zip (e.g., "TX 77063")
		const stateZipMatch = parts[2].match(/([A-Z]{2})\s+(\d{5}(?:-\d{4})?)/)

		if (stateZipMatch) {
			// If it matches the pattern "TX 77063"
			state = stateZipMatch[1] // "TX"
			zip = stateZipMatch[2] // "77063"
		} else {
			// If it doesn't match the pattern, use it as state
			state = parts[2]
		}
	}

	// Check if parts[3] exists and is a zip code
	if (parts[3]) {
		// Check if parts[3] is a zip code
		if (/^\d{5}(-\d{4})?$/.test(parts[3])) {
			zip = parts[3]
		} else if (parts[3] === "USA") {
			// Ignore "USA" as zip
		} else {
			// If not a zip or "USA", it might be part of the address
			zip = ""
		}
	}

	state = Object.keys(workOrderFields.data.state.options).find(
		(key) => workOrderFields.data.state.options[key] === state
	)

	return {
		address,
		city,
		state,
		zip,
	}
}

const toBase64 = (file) => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.onload = () => {
			// Remove the base64 prefix (data:image/jpeg;base64,...)
			const base64 = reader.result.split(",")[1]
			resolve(base64)
		}
		reader.onerror = reject
		reader.readAsDataURL(file)
	})
}

/**
 * Formats a date string or Firebase Timestamp to MM/DD/YYYY format
 * @param {string|Timestamp|Date} dateInput - The date to format (string, Timestamp, or Date object)
 * @returns {string|null} - Formatted date string or null if input is invalid
 */
function formatDate(dateInput) {
	// Return null for empty inputs
	if (!dateInput) {
		return null
	}

	// Check if already in MM/DD/YYYY format using regex
	if (typeof dateInput === "string") {
		const mmddyyyyPattern = /^\d{1,2}\/\d{1,2}\/\d{4}$/
		if (mmddyyyyPattern.test(dateInput)) {
			return dateInput // Already in correct format
		}
	}

	let date

	// Handle Firebase Timestamp
	if (dateInput && typeof dateInput.toDate === "function") {
		// Convert Firebase Timestamp to JavaScript Date
		date = dateInput.toDate()
	}
	// Handle Date object
	else if (dateInput instanceof Date) {
		date = dateInput
	}
	// Handle string or other formats
	else {
		// Try to parse the date string or other input
		date = new Date(dateInput)
	}

	// Check if date is valid
	if (isNaN(date.getTime())) {
		return null // Invalid date
	}

	// Get month, day, and year
	const month = date.getMonth() + 1 // getMonth() returns 0-11
	const day = date.getDate()
	const year = date.getFullYear()

	// Format as MM/DD/YYYY
	return `${month}/${day}/${year}`
}

export const sendJobToPPW = async (
	jobRequest,
	userPlus,
	workOrderFields,
	files
) => {
	const { address, city, state, zip } = parseAddress(
		jobRequest.locationAddress,
		workOrderFields
	)

	const workTypeId = Object.keys(workOrderFields.data.work_type.options).find(
		(key) =>
			workOrderFields.data.work_type.options[key] === jobRequest.serviceType
	)
	// workorder create
	const workOrderData = {
		wo_number: crypto.randomUUID().slice(0, 6),
		address,
		city,
		state,
		zip,
		work_type: "185429",
		client_company:
			jobRequest.companyName === "other" || !jobRequest.companyName
				? "50759" // Quiplo
				: jobRequest.companyName, //if they selected other then we use quiplo and add the new company to the notes
		date_due: formatDate(jobRequest.servicePreferredDates[0].date),
		date_due_client: formatDate(jobRequest.servicePreferredDates[0].date),
		...(jobRequest.lockCode ? { lock_code: jobRequest.lockCode } : {}),
		...(jobRequest.keyCode ? { key_code: jobRequest.keyCode } : {}),
	}

	const workOrderResponse = await axios.post(
		`${config.baseURL}/api/ppw-create-work-order`,
		workOrderData
	)

	//workorder file attach
	if (files.length > 0) {
		files.forEach(async (file) => {
			const fileName = file.name.toLowerCase()
			const isPhoto = /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileName)
			const area = isPhoto ? 0 : 1
			const base64 = await toBase64(file)

			await axios.post(`${config.baseURL}/api/ppw-upload-file`, {
				reportId: workOrderResponse.data.report_id,
				name: file.name,
				document: base64,
				area,
			})
		})
	}

	//job note create
	const jobNoteLines = []
	jobNoteLines.push(`Service Type: ${jobRequest.serviceType}`)
	if (userPlus.userType === "client")
		jobNoteLines.push(`Team Id: ${userPlus.teamId}`)
	if (jobRequest.companyEnteredName)
		jobNoteLines.push(`Company Name: ${jobRequest.companyEnteredName}`)
	if (jobRequest.isDealUnderContract) {
		jobNoteLines.push(
			`Inspection Date: ${formatDate(jobRequest.inspectionPeriod)}`
		)
		jobNoteLines.push(`Close Date: ${formatDate(jobRequest.closingDate)}`)
	}
	jobNoteLines.push(
		`Submission Date: ${new Date().toLocaleDateString("en-US")}`
	)
	jobNoteLines.push(
		`Occupancy Status: ${jobRequest.isPropertyOccupied ? "yes" : "no"}`
	)
	jobNoteLines.push(`Water On? ${jobRequest.isWaterOn ? "yes" : "no"}`)
	if (jobRequest.waterCompany)
		jobNoteLines.push(`Water Company: ${jobRequest.waterCompany}`)
	jobNoteLines.push(`Electric On? ${jobRequest.isElectricOn ? "yes" : "no"}`)
	if (jobRequest.electricCompany)
		jobNoteLines.push(`Electric Company: ${jobRequest.electricCompany}`)
	jobNoteLines.push(`Gas On? ${jobRequest.isGasOn ? "yes" : "no"}`)
	if (jobRequest.gasCompany)
		jobNoteLines.push(`Gas Company: ${jobRequest.gasCompany}`)
	if (jobRequest.lockCodeLocation)
		jobNoteLines.push(`Lockbox Location: ${jobRequest.lockCodeLocation}`)
	if (jobRequest.gateCode)
		jobNoteLines.push(`Gate Code: ${jobRequest.gateCode}`)

	if (userPlus.userType === "client") {
		jobNoteLines.push(`Client Name: ${userPlus.name}`)
		jobNoteLines.push(`Client Email: ${userPlus.email}`)
		jobNoteLines.push(`Client Phone: ${userPlus.phone}`)
	}

	if (jobRequest.currentResidentName) {
		jobNoteLines.push(`Current Resident: ${jobRequest.currentResidentName}`)
		jobNoteLines.push(
			`Current Resident Phone: ${jobRequest.currentResidentPhone}`
		)
		jobNoteLines.push(
			`Current Resident Email: ${jobRequest.currentResidentEmail}`
		)
	}

	if (userPlus.userType === "agent" && !userPlus.isAdmin) {
		jobNoteLines.push(`Agent Name: ${userPlus.name}`)
		jobNoteLines.push(`Agent Email: ${userPlus.email}`)
		jobNoteLines.push(`Agent Phone: ${userPlus.phoneNumber}`)
	}
	if (userPlus.userType === "agent" && userPlus.isAdmin) {
		jobNoteLines.push(`Contact Email: ${jobRequest.userEmail}`)
	}
	jobNoteLines.push(`${jobRequest.jobRequestDetails}`)

	const jobNoteText = jobNoteLines.join("</br>")
	const jobNoteData = {
		report_id: workOrderResponse.data.report_id,
		note_type: "wo",
		note_text: jobNoteText,
		group_permissions: [10807, 13538, 10808, 12631, 13557],
	}

	await axios.post(`${config.baseURL}/api/ppw-job-note-create`, jobNoteData)

	return
}
